/* eslint-disable no-param-reassign */

import Vue from 'vue';
import { mapMutations, mapState } from 'vuex';
import { format } from 'date-fns'; // eslint-disable-line no-unused-vars
import { forEach } from 'lodash'; // eslint-disable-line no-unused-vars
import zenScroll from 'zenscroll';
import Errors from '../classes/Errors';

import Rounds from '../booking/classes/Rounds';
import Flight from '../booking/models/flight';
import Player from '../booking/models/player';
import Timeslots from '../booking/modules/timeslots';
import rules from '../modules/flight-rules';
import Assets from '../booking/classes/Assets';

import { getPrice } from '../booking/modules/helpers'; // eslint-disable-line no-unused-vars
import gms from '../store/gms';

Vue.component('date-time-component', {
    props: {
        countries: {
            type: Array,
            default: () => [],
        },
        languages: {
            type: Array,
            default: () => [],
        },
        locale: {
            type: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            slots: [],
            flightsLoaded: false,
            suntimes: null,
            rounds: new Rounds(this),
            teeTimesUpdating: false,
            dateUpdating: false,
            rules,
            assets: new Assets(this),
            gms,
        };
    },

    computed: {
        filteredCountries() {
            const defaultCountry = _.find(this.countries, { code: 'NL' });
            const disabled = {
                disabled: true,
            };

            return [defaultCountry, disabled, ...this.countries];
        },

        ...mapState('reservation', {
            section: (state) => state.section,
            participants: (state) => state.participants,
            month: (state) => state.month,
            date: (state) => state.date,
            activeRound: (state) => state.activeRound,
            booker: (state) => _.cloneDeep(state.booker),
        }),

        ...mapState('member', {
            memberProfile: (state) => state.profile,
        }),
    },

    watch: {
        date(value) {
            this.handleDate(value);
            this.setSunTimes(value);
        },

        flights(flights) {
            console.log(flights);
        },

        participants() {
            if (this.flightsLoaded) {
                this.handleDate(this.date);
                this.setSunTimes(this.date);
            }
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        //
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        ...mapMutations('reservation', [
            'setSection',
            'addParticipant',
            'removeParticipant',
            'setFlights',
            'setBooker',
            'updateFlightForIndex',
        ]),

        onChangeBookingClick() {
            this.$modals.$emit('show-modal:reset-booking', this.reset.bind(this));
        },

        /**
         * Resets the booking component.
         *
         * @returns {Void}
         */
        reset() {
            window.onbeforeunload = null;
            window.location.reload();
        },

        async handleDate(date) {
            this.slots = Timeslots.createForDate(date);
            this.gms.flights = this.fillFlights(this.participants);
            this.dateUpdating = true;

            try {
                const flight = this.gms.flights[0];
                const { times } = await this.$api.gms.reservation.findStartTimes(
                    this.date,
                    flight,
                    flight.claims[0],
                    this.gms.visitId,
                );

                flight.claims[0].times = Timeslots.getAvailableSlotsForClaimIndex(0, times, flight.claims[0]);
                flight.claims[0].selectedDayPart = Timeslots.getDaypartForFirstAvailableSlot(this.slots, times);

                this.flightsLoaded = true;
                this.dateUpdating = false;

                const element = document.querySelector('[data-flight-holder="0"]');
                if (element) {
                    zenScroll.intoView(element);
                }
            } catch (error) {
                console.error(error);
                this.dateUpdating = false;
            }
        },

        async setSunTimes(date) {
            if (!date) {
                this.suntimes = null;
                return;
            }

            // Fetch suntimes
            try {
                this.suntimes = await this.$api.gms.general.getSunTimesForDate(date);
            } catch (error) {
                console.log(error);
            }
        },

        loadAvailableAssets() {
            this.gms.flights.forEach((flight, index) => {
                // TODO: Check why this was commented out.
                // this.assets.availableForFlight(this.gms.visitId, flight);

                flight.updating = true;

                this.$api.gms.reservation.findAvailableAssets(this.gms.visitId, flight.id)
                .then((data) => {
                    flight.availableAssets = data;
                    flight.updating = false;
                })
                .catch((error) => {
                    console.error(error);
                    flight.updating = false;
                });
            });
        },

        /**
         * Select a timeslot.
         *
         * @param {Object} slot
         * @param {Object} claim
         * @param {Number} flightIndex
         */
        async selectSlot(slot, claim, flightIndex) {
            // Handle click on claimed slot. Remove claim from that slot.
            if (claim.startTime === slot.time && claim.reservationId) {
                this.teeTimesUpdating = true;

                try {
                    await this.$api.gms.reservation.removeClaim(this.gms.visitId, claim.reservationId);
                    claim.reservationId = null;
                    claim.startTime = null;
                    this.reloadStartTimes();
                    this.validateState();
                } catch (error) {
                    this.teeTimesUpdating = false;
                }

                return;
            }

            // If the slot is unvailable, we bail.
            // Should not happen if template is setup correctly.
            if (!this.slotAvailableInClaim(slot, claim)) {
                return;
            }

            // Set time.
            const flight = this.gms.flights[flightIndex];
            this.activeFlight = flightIndex;
            claim.startTime = slot.time;
            const obj = {
                date: this.date,
                nrOfHoles: claim.numberOfHoles,
                startHole: claim.startHole,
                startTime: claim.startTime,
                members: flight.players,
            };

            // Check if the claim has reservationId
            this.teeTimesUpdating = true;
            if (claim.reservationId) {
                // Update the claim
                this.$api.gms.reservation.updateClaim(claim.reservationId, postQs(obj))
                .then((data) => {
                    claim.reservationId = data.reservationId;
                    this.gms.visitId = data.visitId;
                    flight.id = data.flightId;

                    this.reloadStartTimes();
                })
                .catch((error) => {
                    if (error.code === 'time_not_available') {
                        alert(error.message);

                        claim.startTime = null;
                        this.reloadStartTimes();

                        return;
                    }

                    console.log(error);
                    alert('Error creating claim.');
                });

                return;
            }

            // Check if the flight has a flight Id
            if (flight.id) {
                obj.flightId = flight.id;
            }

            // Check if we have a visit Id
            if (this.gms.visitId) {
                obj.visitId = this.gms.visitId;
            }

            // Claim slot
            this.teeTimesUpdating = true;
            this.$api.gms.reservation.createClaim(postQs(obj))
            .then((data) => {
                claim.reservationId = data.reservationId;
                this.gms.visitId = data.visitId;
                flight.id = data.flightId;
                // this.$forceUpdate();
                this.reloadStartTimes();
                this.loadAvailableAssets();

                // TODO: Load next flight times if needed
                flight.locked = true;

                if (this.gms.flights[this.activeFlight + 1]) {
                    this.activeFlight += 1;
                }
            })
            .catch((error) => {
                if (error.code === 'time_not_available') {
                    alert(error.message);

                    claim.startTime = null;
                    this.reloadStartTimes();

                    return;
                }

                console.log(error);
                alert('Error creating claim.');
            });
        },

        setDaypart(flight, dayPart) {
            if (!flight.claims[this.activeRound]) {
                console.error('Claim object does not exist for active round.');
                return;
            }

            flight.claims[this.activeRound].selectedDayPart = dayPart;
        },

        /**
         * Reload the start times for all the flights in the current round.
         *
         * @returns {Void}
         */
        reloadStartTimes() {
            /* eslint-disable no-loop-func */
            let flightCounter = 0;
            for (let index = 0; index < this.gms.flights.length; index++) {
                const flight = this.gms.flights[index];
                const claim = flight.claims[this.activeRound];
                let visitId = null;

                if (this.activeRound > 0) {
                    visitId = this.gms.visitId; // eslint-disable-line prefer-destructuring
                }

                this.$api.gms.reservation.reloadStartTimes(this.date, flight, claim, visitId)
                .then((response) => {
                    flightCounter += 1;
                    if (flightCounter === this.gms.flights.length) {
                        this.$nextTick(() => {
                            this.loadAvailableAssets();
                            this.teeTimesUpdating = false;
                        });
                    }
                }).catch((error) => {});
            }
            /* eslint-enable no-loop-func */
        },

        /**
         * Checks if the timeslot is available for the given
         * claim.
         *
         * @param {Timeslot} slot
         * @param {Number} claimIndex
         */
        slotAvailableInClaim(slot, claim) {
            if (!claim) {
                return false;
            }

            const flatten = _.flatMap(claim.times, 'startTime');
            return (flatten.indexOf(slot.time) !== -1);
        },

        onIncrementClick() {
            // Commit to store.
            this.addParticipant();
        },

        /**
         * Returns the initial flight dataset.
         *
         * @param {Number} playerAmount - The amount of players to distribute over initial flights.
         * @returns {Object}
         */
        fillFlights(playerAmount) {
            // Prepare a general index.
            const playerArray = [];
            const ruleSet = this.rules[playerAmount - 1];
            for (let index = 0; index < playerAmount; index++) {
                const element = playerAmount[index];

                playerArray.push({
                    generalIndex: index,
                });
            }

            const result = [];
            let startIndex = 0;
            const arrangement = ruleSet.flights[0];

            for (let index = 0; index < arrangement.length; index++) {
                const amount = arrangement[index];
                result[index] = [];

                for (let playerIndex = startIndex; playerIndex < (startIndex + amount); playerIndex++) {
                    const player = playerArray[playerIndex];

                    result[index].push(player);
                }

                startIndex += (amount);
            }

            // Create and fill flights with players.
            for (let index = 0; index < result.length; index++) {
                const flight = result[index];

                result[index] = Flight.create({
                    players: this.createPlayersForFlight(flight, index),
                });
            }

            const state = ruleSet.addState || false;
            this.canAddFlight = state;

            return result;
        },

        /**
         * Creates a player dataset for a given flight index.
         *
         * @param {Number} flightIndex
         * @returns {Object}
         */
        createPlayersForFlight(players, flightIndex) {
            const result = [];
            for (let index = 0; index < players.length; index++) {
                const player = players[index];

                if (index === 0 && flightIndex === 0 && this.memberProfile) {
                    result[index] = Player.create({
                        index,
                        generalIndex: player.generalIndex,
                        firstName: this.memberProfile.firstName,
                        lastName: this.memberProfile.lastName,
                        mainBooker: true,
                        uuid: this.memberProfile.uuid,
                        defaultNameState: false,
                    });

                    // eslint-disable-next-line no-continue
                    continue;
                }

                result[index] = Player.create({
                    index,
                    generalIndex: player.generalIndex,
                });
            }

            return result;
        },

        /**
         * Exposes a no times state when:
         *     - There are no times in the flight for the current round
         *     - and when the component is not in an updating state.
         *
         * @returns {Boolean}
         */
        flightHasNoTimes(flight, roundIndex) {
            return flight.claims[roundIndex].times
                && flight.claims[roundIndex].times.length === 0
                && !this.teeTimesUpdating && !flight.claims[roundIndex].reservationId;
        },

        /**
         * Validates the application state by check if the visitId is
         * still valid, and if the flightIds are still valid.
         *
         * @return {Void}
         */
        validateState() {
            // Check if there are flights. If not, nothing more to validate.
            if (this.gms.flights.length < 1) {
                return false;
            }

            this.validateVisitId();

            // Sync flight Ids
            _.each(this.gms.flights, this.validateFlightId);

            const validFlights = this.gms.flights.filter((flight) => flight.id);
            return validFlights.length === this.gms.flights.length;
        },

        validateVisitId() {
            // If there is a visitId but no claim anymore, remove the visitId.
            const validClaims = [];
            this.gms.flights.forEach((flight) => {
                for (let index = 0; index < flight.claims.length; index++) {
                    const claim = flight.claims[index];

                    if (claim.reservationId) {
                        validClaims.push(claim);
                    }
                }
            });

            if (validClaims.length === 0) {
                this.gms.visitId = null;
            }
        },

        validateFlightId(flight) {
            const validFlightClaims = _.filter(flight.claims, (o) => o.reservationId);

            if (_.isEmpty(validFlightClaims)) {
                flight.id = null;
            }
        },

        getPrice,

        onAddGolfsetClicked(flight) {
            this.$modals.$emit('show-modal:golfset', {
                sets: flight.availableAssets.availableGolfsets,
                flight,
                handler: this.onModalGolfsetConfirm.bind(this),
            });
        },

        onModalGolfsetConfirm(flight, set) {
            flight.assetData.golfsetUuids.push(set.uuid);
            flight.assetData.golfsets.push(set);
            this.claimAssetsForFlight(flight);
        },

        onGolfsetRemoveClick(flight, set) {
            this.$modals.$emit('show-modal:remove-golfset', this.removeGolfset.bind(this, flight, set));
        },

        removeGolfset(flight, set) {
            // Delete from uuids
            const index = _.findIndex(flight.assetData.golfsetUuids, ['uuid', set.uuid]);
            flight.assetData.golfsetUuids.splice(index, 1);

            // Delete from golfsets for client-side information
            const setIndex = _.findIndex(flight.assetData.golfsets, ['uuid', set.uuid]);
            flight.assetData.golfsets.splice(setIndex, 1);

            // Re-claim
            this.claimAssetsForFlight(flight);
        },

        claimAssetsForFlight(flight) {
            flight.updating = true;
            this.$api.gms.reservation.claimAssets(this.gms.visitId, flight.id, flight.assetData)
            .then((response) => {
                flight.updating = false;

                // Update availability of all groups.
                for (let index = 0; index < this.gms.flights.length; index++) {
                    // const otherFlight = this.gms.flights[index];
                    this.availableForFlight(index);
                }
            })
            .catch((error) => {
                flight.updating = false;
            });
        },

        availableForFlight(index) {
            const flight = this.gms.flights[index];
            flight.updating = true;

            this.$api.gms.reservation.findAvailableAssets(this.gms.visitId, flight.id)
            .then((data) => {
                // Assets
                flight.availableAssets = data;
                if (!flight.assetData) {
                    // eslint-disable-next-line no-undef
                    flight.assetData = AssetsModel.create();
                }

                flight.updating = false;
            })
            .catch((error) => {
                //
                flight.updating = false;
            });
        },

        onTrolleyValueChange(payload, flight) {
            flight.assetData.trolleys = payload;
            this.claimAssetsForFlight(flight);
        },

        nextSection() {
            if (this.memberProfile) {
                this.setFromMemberProfile(this.memberProfile);
            }

            this.setSection('booker-information');

            Vue.nextTick(() => {
                const element = document.querySelector('.hero-title__intro');
                zenScroll.intoView(element);
            });
        },

        /**
         * Set instance variables from given member profile.
         *
         * @param {Object} memberProfile
         * @returns {Void}
         */
        setFromMemberProfile(memberProfile) {
            this.booker.firstName = memberProfile.firstName;
            this.booker.lastName = memberProfile.lastName;
            this.booker.email = memberProfile.emailAddress;
            this.booker.phoneCountry = { countryCode: memberProfile.countryCode, code: memberProfile.country };
            this.booker.phoneNumber = memberProfile.telephoneNumber;
            this.booker.uuid = memberProfile.uuid;

            this.setBooker(this.booker);
        },

        confirm() {
            if (!this.validateState()) {
                console.log('Could not validate state. There are unclaimed slots in flights.');
                return;
            }

            this.nextSection();
        },
    },
});
